import {scope, type} from 'arktype'
import {klona} from 'klona'

import type {SupportedCountries} from '@/lib/locale/country'
import {fallbackToDefault} from '@/utils/schema/fallback'
import filteredArray from '@/utils/schema/filteredArray'
import simpleStorage from '@/utils/simpleStorage'

import {Charges} from './foodGrubhubModel'

const {Option: FoodGrubhubCartOption} = scope({
  Option: {
    'id': 'string',
    'quantity': 'number',
    'childOptions?': 'Option[] | undefined',
  },
}).export()

export type FoodGrubhubCartOption = typeof FoodGrubhubCartOption.infer

const FoodGrubhubCartItemBase = type({
  'restaurantId': 'string',
  'menuItemId': 'string',
  'quantity': 'number',
  'options?': FoodGrubhubCartOption.array().or(type.undefined),
  'specialInstructions?': 'string | undefined',
  'moreInfo?': {
    'restaurantName?': 'string | undefined',
    'restaurantImgUrl?': 'string | undefined',
    'restaurantAddress?': 'string | undefined',
    'restaurantPhone?': 'string | undefined',
    'menuItemImgUrl?': 'string | undefined',
  },
})

const FoodGrubhubCartItemWithoutQuantityBase = FoodGrubhubCartItemBase.omit('quantity')
const FoodGrubhubCartItemWithoutQuantityAndMoreInfoBase =
  FoodGrubhubCartItemWithoutQuantityBase.omit('moreInfo')

export const FoodGrubhubCartItem = FoodGrubhubCartItemBase.pipe.try(data => {
  // Recursively normalize options to ensure childOptions are always arrays
  function normalizeOptions(options: FoodGrubhubCartOption[]): FoodGrubhubCartOption[] {
    return options.map(option => ({
      ...option,
      childOptions: option.childOptions ? normalizeOptions(option.childOptions) : [],
    }))
  }

  return {
    ...data,
    options: (() => {
      if (data.options === undefined || data.options.length === 0) {
        return []
      }

      const newOptions = klona(data.options)
      return normalizeOptions(newOptions)
    })(),
    specialInstructions: data.specialInstructions ?? '',
  }
})
export type FoodGrubhubCartItem = typeof FoodGrubhubCartItem.infer
export const FoodGrubhubCartItemWithoutQuantity = FoodGrubhubCartItemWithoutQuantityBase.pipe.try(
  data => {
    // Recursively normalize options to ensure childOptions are always arrays
    function normalizeOptions(options: FoodGrubhubCartOption[]): FoodGrubhubCartOption[] {
      return options.map(option => ({
        ...option,
        childOptions: option.childOptions ? normalizeOptions(option.childOptions) : [],
      }))
    }

    return {
      ...data,
      options: (() => {
        if (data.options === undefined || data.options.length === 0) {
          return []
        }

        const newOptions = klona(data.options)
        return normalizeOptions(newOptions)
      })(),
      specialInstructions: data.specialInstructions ?? '',
    }
  },
)
export type FoodGrubhubCartItemWithoutQuantity = typeof FoodGrubhubCartItemWithoutQuantity.infer

export const FoodGrubhubCartItemWithoutQuantityAndMoreInfo =
  FoodGrubhubCartItemWithoutQuantityAndMoreInfoBase.pipe.try(data => {
    // Recursively normalize options to ensure childOptions are always arrays
    function normalizeOptions(options: FoodGrubhubCartOption[]): FoodGrubhubCartOption[] {
      return options.map(option => ({
        ...option,
        childOptions: option.childOptions ? normalizeOptions(option.childOptions) : [],
      }))
    }

    return {
      ...data,
      options: (() => {
        if (data.options === undefined || data.options.length === 0) {
          return []
        }

        const newOptions = klona(data.options)
        return normalizeOptions(newOptions)
      })(),
      specialInstructions: data.specialInstructions ?? '',
    }
  })
export type FoodGrubhubCartItemWithoutQuantityAndMoreInfo =
  typeof FoodGrubhubCartItemWithoutQuantityAndMoreInfo.infer

export const FoodGrubhubCartV1 = type({
  'foodCartItems': fallbackToDefault(filteredArray(FoodGrubhubCartItem), []),
  'moreInfo?': {
    'partnerCartId?': 'string | undefined',
    'charges?': Charges.or(type.undefined),
    'orderingInfo?': type({
      estimateRange: {
        minimum: 'number',
        maximum: 'number',
      },
      // 'type': 'string',
    }).or(type.undefined),
    'errorDetails?': type({
      // 'property?': 'string | null | undefined',
      messageKey: 'string',
    }).array(),
  },
  'version': 'number',
})

export type FoodGrubhubCartV1 = typeof FoodGrubhubCartV1.infer

export function getDefaultFoodGrubhubCart(version = 1): FoodGrubhubCartV1 {
  return {
    foodCartItems: [],
    version,
  }
}

export const FOOD_GRUBHUB_CART_KEY = `guest-food-grubhub-cart`

function getFoodGrubhubCartKey(country: SupportedCountries) {
  return `${FOOD_GRUBHUB_CART_KEY}-${country}`
}

function getFoodGrubhubCart(country: SupportedCountries): FoodGrubhubCartV1 {
  try {
    return FoodGrubhubCartV1(simpleStorage.get(getFoodGrubhubCartKey(country)))
  } catch {
    return getDefaultFoodGrubhubCart()
  }
}

function updateFoodGrubhubCart({
  country,
  foodCart,
}: {
  country: SupportedCountries
  foodCart: FoodGrubhubCartV1
}) {
  simpleStorage.set(getFoodGrubhubCartKey(country), foodCart)
  return foodCart
}

function clearFoodGrubhubCart(country: SupportedCountries) {
  simpleStorage.remove(getFoodGrubhubCartKey(country))
}

export default {getFoodGrubhubCart, updateFoodGrubhubCart, clearFoodGrubhubCart}
